/*
 * Decoration zones for the Decorate configurator.
 *
 * Product photos are transparent, upright, front-facing PNGs at different scales, so a zone is
 * defined relative to the garment, not to the image: we find the garment's bounding box from
 * the photo's alpha channel and express every zone as fractions of the garment HEIGHT
 * (dx = horizontal offset of the zone centre from the garment centre, top = distance from the
 * garment top, w/h = zone size). Height is the stable scale; width is not (sleeves).
 * "Left chest" is the wearer's left, which is the viewer's right.
 *
 * Zone keys are CMS location codes (FC, LC, RC, FCZC, LHP, DLL, BKT, …). A product can override
 * the geometry with the JSON metafield decorate.locations:
 *   [{"key":"FC","label":"Full Chest","dx":0,"top":0.2,"w":0.4,"h":0.44,"inches":"12 x 13"}]
 * (an array replaces every zone), or patch single zones and keep the rest:
 *   {"FC":[0,0.15,0.36,0.34]}   (dx, top, w, h)
 */
(function () {
  // Keys are CMS location codes (cms_decoration_locations.code). Labels and inches here are
  // fallbacks; the CMS values (product metafield decorate.location_codes) win when present.
  const L = {
    FC: { label: 'Full Chest', inches: [12, 13] },
    FCZC: { label: 'Full Chest (zipper covered)', inches: [12, 8.5] },
    LC: { label: 'Left Chest', inches: [4, 4] },
    RC: { label: 'Right Chest', inches: [4, 4] },
    LHP: { label: 'Left Hip', inches: [3.5, 3.5] },
    RHP: { label: 'Right Hip', inches: [3.5, 3.5] },
    DLL: { label: 'Down Left Leg', inches: [5, 18] },
    DRL: { label: 'Down Right Leg', inches: [5, 18] },
    LL: { label: 'Left Leg', inches: [4, 4] },
    RL: { label: 'Right Leg', inches: [4, 4] },
    BKT: { label: 'Blanket Standard', inches: [20, 15] },
    FB: { label: 'Full Back', inches: [12, 14] },
    ULA: { label: 'Upper Left Arm', inches: [2.75, 4] },
    URA: { label: 'Upper Right Arm', inches: [2.75, 4] },
    DLS: { label: 'Down Left Sleeve', inches: [2.5, 13] },
    DRS: { label: 'Down Right Sleeve', inches: [2.5, 13] },
    CFL: { label: 'Cuff Left', inches: [2.75, 4] },
    CFR: { label: 'Cuff Right', inches: [2.75, 4] },
    SHD: { label: 'Side Hood', inches: [3, 3] },
    THD: { label: 'Top Hood', inches: [3, 3] },
    BCT: { label: 'Blanket Center', inches: [20, 15] },
  };

  // [dx, top, w, h, inches?] as fractions of garment height; positive dx = viewer's right
  // (the wearer's left).
  // Sleeve zones follow the photo's outline: {edge: +1 (viewer's right = wearer's left) or -1,
  // inset from that edge, top, w, h}, all × garment height. Long sleeves hang at the sides of the
  // front photo; a tee's short sleeve only has room for the upper arm.
  const sleeve = (side, inset, top, w, h) => ({ edge: side, inset, top, w, h });
  const LONG_SLEEVES = (top) => ({
    ULA: sleeve(1, 0.045, top, 0.06, 0.1), URA: sleeve(-1, 0.045, top, 0.06, 0.1),
    DLS: sleeve(1, 0.035, top + 0.12, 0.05, 0.48), DRS: sleeve(-1, 0.035, top + 0.12, 0.05, 0.48),
    CFL: sleeve(1, 0.035, 0.86, 0.06, 0.08), CFR: sleeve(-1, 0.035, 0.86, 0.06, 0.08),
  });
  const HOOD = { SHD: [0.12, 0.05, 0.07, 0.07], THD: [0, 0.005, 0.08, 0.05] };
  // The back view is drawn from the front photo (see backImage); FB sits on it.
  const BACK = (top, h) => ({ FB: { view: 'back', dx: 0, top, w: 0.42, h } });

  // On the CMS Side lay-down (a three-quarter view of the wearer's left side) the near sleeve runs
  // down the right-hand edge. The right side is the same shot mirrored, so its zones follow the
  // left-hand edge. Used whenever the colorway has a Side shot; otherwise the front-photo
  // geometry below applies.
  const SIDE_LONG = (top) => ({
    ULA: sleeve(1, 0.05, top, 0.07, 0.1), DLS: sleeve(1, 0.04, top + 0.12, 0.06, 0.5), CFL: sleeve(1, 0.04, 0.85, 0.07, 0.08),
  });
  const SIDES = {
    top: { ULA: sleeve(1, 0.09, 0.12, 0.09, 0.1) },
    crew: SIDE_LONG(0.12),
    qzip: SIDE_LONG(0.13),
    hood: { ...SIDE_LONG(0.18), SHD: [0.05, 0.04, 0.09, 0.09] },
    zip: SIDE_LONG(0.13),
    ziphood: { ...SIDE_LONG(0.18), SHD: [0.05, 0.04, 0.09, 0.09] },
  };
  const MIRROR = { ULA: 'URA', DLS: 'DRS', CFL: 'CFR' };

  const SHAPES = {
    top: {
      FC: [0, 0.2, 0.4, 0.44],
      LC: [0.14, 0.2, 0.13, 0.13],
      RC: [-0.14, 0.2, 0.13, 0.13],
      ULA: sleeve(1, 0.1, 0.17, 0.08, 0.1), URA: sleeve(-1, 0.1, 0.17, 0.08, 0.1),
      ...BACK(0.16, 0.5),
    },
    tank: {
      FC: [0, 0.2, 0.4, 0.44],
      LC: [0.14, 0.2, 0.13, 0.13],
      RC: [-0.14, 0.2, 0.13, 0.13],
      ...BACK(0.16, 0.5),
    },
    crew: {
      FC: [0, 0.2, 0.4, 0.44],
      LC: [0.14, 0.2, 0.13, 0.13],
      RC: [-0.14, 0.2, 0.13, 0.13],
      ...LONG_SLEEVES(0.13),
      ...BACK(0.16, 0.5),
    },
    qzip: {
      FC: [0, 0.3, 0.38, 0.3, [12, 9]],
      LC: [0.15, 0.22, 0.12, 0.12],
      RC: [-0.15, 0.22, 0.12, 0.12],
      ...LONG_SLEEVES(0.14),
      ...BACK(0.18, 0.45),
    },
    hood: {
      FC: [0, 0.32, 0.34, 0.24, [12, 8.5]],
      LC: [0.13, 0.32, 0.11, 0.11],
      RC: [-0.13, 0.32, 0.11, 0.11],
      ...LONG_SLEEVES(0.2),
      ...HOOD,
      ...BACK(0.26, 0.48),
    },
    zip: {
      FC: [0, 0.24, 0.36, 0.3, [12, 10]],
      FCZC: [0, 0.24, 0.36, 0.3, [12, 10]],
      LC: [0.16, 0.22, 0.13, 0.13],
      RC: [-0.16, 0.22, 0.13, 0.13],
      ...LONG_SLEEVES(0.14),
      ...BACK(0.18, 0.45),
    },
    vest: {
      LC: [0.16, 0.22, 0.13, 0.13],
      RC: [-0.16, 0.22, 0.13, 0.13],
      ...BACK(0.18, 0.45),
    },
    ziphood: {
      FCZC: [0, 0.32, 0.32, 0.22],
      FC: [0, 0.32, 0.32, 0.22, [12, 8.5]],
      LC: [0.15, 0.32, 0.11, 0.11],
      RC: [-0.15, 0.32, 0.11, 0.11],
      ...LONG_SLEEVES(0.2),
      ...HOOD,
      ...BACK(0.26, 0.48),
    },
    // About 50 in per garment height: hips 3.5 x 3.5, down-leg strips 5 x 18 (the POD Front Hip and
    // Front Thigh rows the codes alias to).
    pant: {
      LHP: [0.11, 0.1, 0.07, 0.07],
      RHP: [-0.11, 0.1, 0.07, 0.07],
      DLL: [0.12, 0.2, 0.1, 0.36],
      DRL: [-0.12, 0.2, 0.1, 0.36],
    },
    short: {
      LL: [0.17, 0.22, 0.13, 0.2],
      RL: [-0.17, 0.22, 0.13, 0.2],
    },
    bag: { FC: [0, 0.33, 0.3, 0.25, [10, 8]] },
    blanket: {
      BKT: [0, 0.22, 0.3, 0.3],
      BCT: [0, 0.22, 0.3, 0.3],
    },
  };

  // Locations that cover the same cloth; choosing one blocks the others.
  const CONFLICTS = {
    FC: ['LC', 'RC', 'FCZC'],
    FCZC: ['LC', 'RC', 'FC'],
    LC: ['FC', 'FCZC'],
    RC: ['FC', 'FCZC'],
    LHP: ['DLL'], DLL: ['LHP'],
    RHP: ['DRL'], DRL: ['RHP'],
    BKT: ['BCT'], BCT: ['BKT'],
    ULA: ['DLS'], DLS: ['ULA'],
    URA: ['DRS'], DRS: ['URA'],
  };

  function shapeFor(product) {
    // The CMS style group ("Mens Zip Hood Covered") is the best hint; titles often omit "hood".
    const h = `${product.handle} ${product.title} ${product.group || ''} ${product.styleGroup || ''}`.toLowerCase();
    if (h.includes('blanket')) return 'blanket';
    if (h.includes('bag')) return 'bag';
    if (h.includes('short')) return 'short';
    if (h.includes('pant') || h.includes('jogger')) return 'pant';
    if (h.includes('q-zip') || h.includes('q zip') || h.includes('quarter') || h.includes('1/4') || h.includes('pullover') && h.includes('zip')) return 'qzip';
    const fullZip = h.includes('full-zip') || h.includes('full zip') || h.includes('zip-hood') || h.includes('zip hood') || h.includes('jacket') || h.includes('vest') || h.includes('windbreaker');
    if (fullZip && h.includes('hood')) return 'ziphood';
    if (h.includes('vest')) return 'vest';
    if (fullZip) return 'zip';
    if (h.includes('hood')) return 'hood';
    if (h.includes('tank') || h.includes('poncho')) return 'tank';
    if (/long-sleeve|long sleeve|crew|sweatshirt|raglan|pullover/.test(h)) return 'crew';
    return 'top';
  }

  // product.locations: optional geometry override (metafield decorate.locations).
  // product.cmsLocations: [{code,name,w,h,cats}] from the CMS (metafield decorate.location_codes);
  // when present, only those codes are offered, with the CMS names and sizes.
  const geom = (v) => (Array.isArray(v) ? { dx: v[0], top: v[1], w: v[2], h: v[3], inches: v[4] } : v);

  // hasSide: the colorway has a Side lay-down, so sleeve and side-hood codes move onto it.
  function definitions(product, hasSide) {
    if (Array.isArray(product.locations) && product.locations.length) {
      return product.locations.map((z) => ({
        key: z.key,
        label: z.label || (L[z.key] && L[z.key].label) || z.key,
        dx: +z.dx || 0, top: +z.top, w: +z.w, h: +z.h,
        inches: z.inches || (L[z.key] ? L[z.key].inches.join(' x ') : ''),
        cats: z.cats || null,
      }));
    }
    const sh = shapeFor(product), shape = SHAPES[sh];
    const side = hasSide ? SIDES[sh] || {} : {};
    const sideGeom = (k) => {
      if (side[k]) return { ...geom(side[k]), view: 'left' };
      const src = Object.keys(MIRROR).find((x) => MIRROR[x] === k);
      if (src && side[src]) { const g = geom(side[src]); return { ...g, edge: g.edge ? -g.edge : g.edge, dx: -(g.dx || 0), view: 'right' }; }
      return null;
    };
    const cms = Array.isArray(product.cmsLocations) && product.cmsLocations.length ? product.cmsLocations : null;
    const keys = cms ? cms.map((c) => c.code).filter((k) => shape[k] || sideGeom(k)) : Object.keys(shape);
    // An OBJECT override patches single zones on top of the shape: {"FC":[dx,top,w,h]} moves Full Chest
    // only (e.g. above a front pouch pocket); every other zone keeps its computed geometry and view.
    const patch = product.locations && !Array.isArray(product.locations) && typeof product.locations === 'object' ? product.locations : {};
    return keys.map((key) => {
      const g = (patch[key] && geom(patch[key])) || sideGeom(key) || geom(shape[key]);
      const c = cms && cms.find((x) => x.code === key);
      let ins = c && c.w && c.h ? [c.w, c.h] : g.inches || L[key].inches;
      // The CMS states sleeve strips lying flat (13 × 2.5); on a hanging sleeve the zone is tall.
      if ((g.h / g.w > 1.5 && ins[0] > ins[1]) || (g.w / g.h > 1.5 && ins[1] > ins[0])) ins = [ins[1], ins[0]];
      return { key, label: (c && cleanName(c.name)) || L[key].label, ...g, view: g.view || 'front', inches: `${ins[0]} x ${ins[1]}`, cats: c ? c.cats : null };
    });
  }

  // CMS locations this style accepts that the front photo cannot show (back, sleeves, hood…).
  function offPhoto(product, hasSide) {
    const keys = new Set(definitions({ ...product, locations: null }, hasSide).map((d) => d.key));
    const cms = Array.isArray(product.cmsLocations) ? product.cmsLocations : [];
    // Only offer what the garment physically has: no sleeve prints on a vest or tank, no hood prints without a hood.
    const shape = shapeFor(product);
    const noSleeves = ['vest', 'tank', 'blanket', 'bag', 'pant', 'short'].includes(shape);
    const noHood = !['hood', 'ziphood'].includes(shape);
    const sleeve = /^(ULA|URA|DLS|DRS|CFL|CFR)$/, hood = /^(SHD|THD)$/;
    return cms.filter((c) => !keys.has(c.code) && !(noSleeves && sleeve.test(c.code)) && !(noHood && hood.test(c.code))).map((c) => cleanName(c.name));
  }

  // The right side: the Side lay-down mirrored.
  function mirror(img) {
    const c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height;
    const ctx = c.getContext('2d');
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(img, 0, 0);
    return c;
  }

  // CMS names carry the garment ("Down Left Leg Pants"); the page already says what it is.
  function cleanName(s) {
    return titleCase(s).replace(/\s+(Pants|Shorts)$/, '');
  }

  function titleCase(s) {
    return String(s || '').replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
  }

  // Bounding box of the opaque pixels of an image, in image pixels.
  function measure(img) {
    const s = 300;
    const c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, s, s);
    const data = ctx.getImageData(0, 0, s, s).data;
    let top = s, bottom = -1, left = s, right = -1, opaque = false;
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        if (data[(y * s + x) * 4 + 3] > 20) {
          opaque = true;
          if (y < top) top = y; if (y > bottom) bottom = y;
          if (x < left) left = x; if (x > right) right = x;
        }
      }
    }
    // A photo without transparency: assume the garment fills the middle 80%.
    if (!opaque || (top === 0 && left === 0 && bottom === s - 1 && right === s - 1)) {
      top = 0.1 * s; bottom = 0.9 * s; left = 0.1 * s; right = 0.9 * s;
    }
    const k = (img.naturalWidth || img.width) / s;
    // Outermost opaque column on each row, for zones that follow the sleeve edge.
    const edges = [];
    for (let y = 0; y < s; y++) {
      let l = -1, r = -1;
      for (let x = 0; x < s; x++) if (data[(y * s + x) * 4 + 3] > 20) { if (l < 0) l = x; r = x; }
      edges.push(l < 0 ? null : [l * k, r * k]);
    }
    const edgeAt = (yPx, side) => {
      const row = Math.min(s - 1, Math.max(0, Math.round(yPx / k)));
      for (let d = 0; d < 20; d++) {
        const e = edges[Math.min(s - 1, row + d)] || edges[Math.max(0, row - d)];
        if (e) return side > 0 ? e[1] : e[0];
      }
      return side > 0 ? (left + (right - left)) * k : left * k;
    };
    return { top: top * k, H: (bottom - top) * k, left: left * k, W: (right - left) * k, edgeAt };
  }

  // Zones in image pixels: {key,label,inches,size:[w_in,h_in],x,y,w,h,cx,cy}
  // imgs: {front, back, left, right} images or canvases (back/left/right may be missing).
  function compute(product, imgs) {
    if (!imgs.front) imgs = { front: imgs };
    const ms = {};
    const mOf = (v) => ms[v] || (ms[v] = measure(imgs[v] || imgs.front));
    return definitions(product, !!(imgs.left && imgs.right)).map((d) => {
      const view = d.view || 'front';
      const m = mOf(view), cx = m.left + m.W / 2;
      const w = d.w * m.H, h = d.h * m.H;
      const size = String(d.inches).split(/x|×/).map((n) => parseFloat(n) || 0);
      const y = m.top + d.top * m.H;
      const zcx = d.edge ? m.edgeAt(y + h / 2, d.edge) - d.edge * d.inset * m.H : cx + d.dx * m.H;
      const x = zcx - w / 2;
      // Sleeve zones lean with the sleeve: the outline's slope over the zone's height, in degrees
      // (CSS/canvas rotate, clockwise positive). Sleeves and hood sides are round: `curve`.
      let angle = 0;
      if (d.edge) {
        const e1 = m.edgeAt(y, d.edge), e2 = m.edgeAt(y + Math.max(h, 0.12 * m.H), d.edge);
        angle = Math.max(-25, Math.min(25, (-Math.atan2(e2 - e1, Math.max(h, 0.12 * m.H)) * 180) / Math.PI));
      }
      const curve = /^(ULA|URA|DLS|DRS|CFL|CFR|SHD)$/.test(d.key);
      return { key: d.key, label: d.label, inches: d.inches, cats: d.cats, view, size, x, y, w, h, cx: x + w / 2, cy: y + h / 2, angle, curve };
    });
  }

  // A plain back for the Back view, made from the front photo: the garment's own color, a faint
  // trace of the photo's shading (so folds and seams still read), clipped sharply to the
  // garment's outline. Front details (drawstrings, pockets, hood opening) mostly disappear.
  function backImage(img) {
    const W = img.naturalWidth || img.width, H = img.naturalHeight || img.height;
    const rgb = averageColor(img);
    const small = document.createElement('canvas');
    small.width = Math.max(8, Math.round(W / 24)); small.height = Math.max(8, Math.round(H / 24));
    const sctx = small.getContext('2d');
    sctx.fillStyle = `rgb(${rgb})`;
    sctx.fillRect(0, 0, small.width, small.height);
    sctx.drawImage(img, 0, 0, small.width, small.height);
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    ctx.fillStyle = `rgb(${rgb})`;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 0.45;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(small, 0, 0, W, H);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(img, 0, 0);
    return c;
  }

  // Drawstrings (laces) hanging over the chest of a hood, as a mask canvas the size of the photo
  // (opaque where a lace is). Prints are cut away there so the laces hang in front of them, as on
  // a real garment. Found as long, thin vertical ridges in brightness that start near the collar;
  // a centred ridge that runs past the chest is the zipper and is left alone (a print may cover
  // it). Null for garments without a hood.
  function laceMask(img, product) {
    const sh = shapeFor(product);
    if (sh !== 'hood' && sh !== 'ziphood') return null;
    const m = measure(img), H = m.H, cx = m.left + m.W / 2, k = 0.5;
    const X0 = Math.round(cx - 0.2 * H), X1 = Math.round(cx + 0.2 * H), Y0 = Math.round(m.top + 0.12 * H), Y1 = Math.round(m.top + 0.62 * H);
    const w = Math.round((X1 - X0) * k), h = Math.round((Y1 - Y0) * k);
    if (w < 20 || h < 20) return null;
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, X0, Y0, X1 - X0, Y1 - Y0, 0, 0, w, h);
    const px = ctx.getImageData(0, 0, w, h).data, N = w * h, L = new Float32Array(N);
    for (let i = 0; i < N; i++) L[i] = 0.299 * px[i * 4] + 0.587 * px[i * 4 + 1] + 0.114 * px[i * 4 + 2];
    const boxV = (a, r) => { const o = new Float32Array(N); for (let x = 0; x < w; x++) { let s2 = 0, n = 0; for (let y = -r; y < h + r; y++) { if (y + r < h) { s2 += a[(y + r) * w + x]; n++; } if (y - r - 1 >= 0) { s2 -= a[(y - r - 1) * w + x]; n--; } if (y >= 0 && y < h) o[y * w + x] = s2 / n; } } return o; };
    const boxH = (a, r) => { const o = new Float32Array(N); for (let y = 0; y < h; y++) { let s2 = 0, n = 0; for (let x = -r; x < w + r; x++) { if (x + r < w) { s2 += a[y * w + x + r]; n++; } if (x - r - 1 >= 0) { s2 -= a[y * w + x - r - 1]; n--; } if (x >= 0 && x < w) o[y * w + x] = s2 / n; } } return o; };
    const ridge = (sm, r) => { const o = new Float32Array(N); for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { const i = y * w + x; o[i] = Math.abs(sm[i] - (sm[y * w + Math.max(0, x - r)] + sm[y * w + Math.min(w - 1, x + r)]) / 2); } return o; };
    const median = (a) => { const t = []; for (let i = 0; i < N; i += 7) t.push(a[i]); t.sort((p, q) => p - q); return t[t.length >> 1] || 0; };
    // Coarse: strands.
    let resp = boxH(boxV(ridge(boxV(L, 8), 5), 10), 1);
    const t1 = median(resp) * 4 + 2, coarse = new Uint8Array(N);
    for (let i = 0; i < N; i++) coarse[i] = resp[i] > t1 ? 1 : 0;
    const lab = new Int32Array(N), comps = [];
    for (let i = 0; i < N; i++) {
      if (!coarse[i] || lab[i]) continue;
      const id = comps.length + 1, st = [i];
      let x0 = w, x1 = 0, y0 = h, y1 = 0, sx = 0, n = 0;
      lab[i] = id;
      while (st.length) {
        const j = st.pop(), x = j % w, y = (j / w) | 0;
        x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); sx += x; n++;
        for (const q of [j - 1, j + 1, j - w, j + w]) if (q >= 0 && q < N && coarse[q] && !lab[q] && Math.abs((q % w) - x) <= 1) { lab[q] = id; st.push(q); }
      }
      const xc = X0 + (sx / n) / k;
      const lace = y0 < h * 0.25 && y1 - y0 > h * 0.45 && x1 - x0 < 0.12 * w + 15 && !(Math.abs(xc - cx) < 0.03 * H && y1 > h * 0.95);
      comps.push(lace);
    }
    // Fine: the lace pixels inside each strand.
    const fine = boxH(boxV(ridge(boxV(L, 4), 3), 4), 1), t2 = median(fine) * 3 + 1.2;
    let keep = new Uint8Array(N);
    const near = (i, r) => { const x = i % w, y = (i / w) | 0; for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) { const xx = x + dx, yy = y + dy; if (xx >= 0 && xx < w && yy >= 0 && yy < h) { const l = lab[yy * w + xx]; if (l && comps[l - 1]) return true; } } return false; };
    let any = false;
    for (let i = 0; i < N; i++) if (fine[i] > t2 && near(i, 2)) { keep[i] = 1; any = true; }
    if (!any) return null;
    const grow = (a, r, want) => { const o = new Uint8Array(N); for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) { let v = want ? 0 : 1; for (let dy = -r; dy <= r && v !== want; dy++) for (let dx = -r; dx <= r; dx++) { const xx = x + dx, yy = y + dy; const b = xx >= 0 && xx < w && yy >= 0 && yy < h ? a[yy * w + xx] : 0; if (b === want) { v = want; break; } } o[y * w + x] = v; } return o; };
    keep = grow(grow(grow(keep, 2, 1), 2, 0), 1, 1); // close small gaps, then a little margin
    const mk = ctx.createImageData(w, h);
    for (let i = 0; i < N; i++) { mk.data[i * 4 + 3] = keep[i] ? 255 : 0; }
    ctx.clearRect(0, 0, w, h);
    ctx.putImageData(mk, 0, 0);
    const out = document.createElement('canvas');
    out.width = img.naturalWidth || img.width; out.height = img.naturalHeight || img.height;
    const o = out.getContext('2d');
    o.imageSmoothingEnabled = true;
    o.drawImage(c, X0, Y0, X1 - X0, Y1 - Y0);
    return out;
  }

  // Mean color of the opaque middle of the photo, as "r,g,b".
  function averageColor(img, skip) {
    const s = 60, c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, s, s);
    if (skip) { ctx.globalCompositeOperation = 'destination-out'; ctx.drawImage(skip, 0, 0, s, s); ctx.globalCompositeOperation = 'source-over'; }
    const d = ctx.getImageData(s * 0.3, s * 0.35, s * 0.4, s * 0.4).data;
    const t = [0, 0, 0];
    let n = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) continue;
      t[0] += d[i]; t[1] += d[i + 1]; t[2] += d[i + 2]; n++;
    }
    return n ? t.map((v) => Math.round(v / n)).join(',') : '200,200,200';
  }

  // Is the garment light (dark ink reads best) or dark? Averages the opaque middle of the photo.
  function isLight(img) {
    const s = 60, c = document.createElement('canvas');
    c.width = s; c.height = s;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, s, s);
    const d = ctx.getImageData(s * 0.3, s * 0.3, s * 0.4, s * 0.4).data;
    let sum = 0, n = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) continue;
      sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
      n++;
    }
    return n ? sum / n > 150 : true;
  }

  window.DecorateZones = { compute, definitions, offPhoto, shapeFor, isLight, backImage, mirror, averageColor, laceMask, CONFLICTS };
})();
